import * as React from "react";
import { connect } from "react-redux";
import { IAppointmentScheduleResponse, IPrescription, IActionWithContext, IAlbusContext } from "@curefit/care-common";
import { ModalComponent } from "../../components/ModalComponent";
import { PatientCompleteInfoActions } from "../../actions/PatientCompleteInfoActions";
import { default as AppUtils, LOADING_STATE } from "../../utils/AppUtils";
import { isEmpty as _isEmpty } from "lodash";
import IPatientCompleteInfo from "../../models/patients/IPatientCompleteInfo";
import { IOngoingConsultation } from "../../models/IOngoingConsultation";
import {
    AppConstants,
    AppMessages,
    metricIdsToFetchMetrics,
    ConsultationStatusConstant, EditPrescriptionTabKeyName, PrescriptionTabModes
} from "../../utils/AppConstants";
import { ConsultationTabNames, TabConfig, TabManager } from "../../utils/TabManager"
import { IAppState } from "../../models/IAppState";
import IDoctorProfile from "../../models/IDoctorProfile";
import { IPatientVitalResponse } from "../../models/IPatientVitalResponse";
import * as RouteConfigs from "../../routes";
import IAccountData from "../../models/IAccountData";
import IMedicineType from "../../models/staticData/IMedicineType";
import { TwilioActions } from "../../actions/TwilioActions";
import PdfSaveFailureModal from "../Ongoing/pdfSaveFailureModal";
import { ApiManager } from "../../api/ApiManager";
import { AlbusUrlConfigs } from "../../api/AlbusUrlConfigs";
import htmlDocTemplate from "../../PdfTemplates/prescription";
import { IConstituentTemplate, IPdfTemplates } from "../../models/PdfTemplatesModels";
import { ORION_IMG_URL } from "../../utils/AppConstants";
import { IEHRSavePatientMedicalProfile } from "../../models/patients/IEHRSavePatientMedicalProfile";
import { EditPrescriptionActions } from "../../actions/EditPrescriptionActions";
import { IEditConsultationResponse } from "../../models/IEditConsultationResponse";
import CommonPatientPrescriptionTab from "../../components/ongoing/CommonPatientPrescriptionTab";
import { Goal } from "@curefit/gmf-common";
import IMetric from "../../models/metrics/IMetric";
import IPatientLifestylePlanWrapper from "../../models/patients/IPatientLifestylePlanWrapper";
import TopBanner from "../Ongoing/TopBanner";
import TabsHeader from "../../components/common/TabsHeader";
import EditPrescriptionReviewPdf from "../../components/EditPrescriptionReviewPdf";
import { IFeatureConfig } from "../../models/IFeatureConfig";
import { EventNames, SectionContext, FlowContext } from "../../utils/EventTrackerConstants";
import { TreatmentPlanActions } from "../../actions/TreatmentPlanActions";
import IDoctorSpeciality from "../../models/IDoctorSpeciality";

export interface PatientConsultationEditContainerProps {
    params: any,
    accountData: IAccountData,
    uploadPrescription: (appointmentId: number, prescription: File, type: string) => Promise<any>,
    uploadPlan: (appointmentId: number, plan: File, type: string) => Promise<any>,
    patientDetails: IPatientCompleteInfo,
    doctorProfile: IDoctorProfile,
    patientVitals: IPatientVitalResponse[],
    staticMedicineTypes: IMedicineType[],
    staticSpecialities: IDoctorSpeciality[],
    fetchPatientVitals: (patientId: number, userId: number, metricIds: number[]) => any,
    editConsultation: IOngoingConsultation,
    clearCurrentChannel: () => void;
    fetchConsultationPdfTemplates: (appointmentSchedule: IAppointmentScheduleResponse) => Promise<IConstituentTemplate>,
    pdfTemplates: IPdfTemplates,
    suggestions: { [key: string]: any }
    clearEditPrescriptionDetails: () => void
    fetchEditConsultationDetails: (appointmentId: number, patientId: number, userId: number, isSelf: boolean, doctorProfile: IDoctorProfile, subServiceType: string) => Promise<IEditConsultationResponse>,
    staticGoals: Goal[];
    staticMetrics: IMetric[];
    savePatientPlan: (
        consultation: IAppointmentScheduleResponse,
        prescription: IPrescription,
        patientPlan: IPatientLifestylePlanWrapper,
        planId: string,
        isSelf?: boolean,
        medicalProfile?: any
    ) => Promise<any>;
    updateCurrentConsultationData: (medicalProfile: IEHRSavePatientMedicalProfile) => void
    updateEditConsultationDetails: (response: IEditConsultationResponse) => void,
    getTreatmentPlans: (doctorId: number) => void,
    fetchDataOnSelect: (doctorId: number, diagnosis?: Array<string>) => void,
    featureConfigInfo: IFeatureConfig
}

export interface PatientConsultationEditContainerState {
    consultationDetailsLoadingState: LOADING_STATE,
    modalStatus: boolean,
    modalScreen: string,
    prescriptionSaveError?: string,
    pdfData: File,
    pdfPreviewData: File,
    isPdfDataSaved: boolean,
    errorMessage: string,
    secondModal: boolean,
    hasFetchedTemplates: boolean,
    hasFetchedTreatmentPlan: boolean,
    allTabs: TabConfig[],
    activeTab: TabConfig
}

export const MODAL_SCREENS = {
    1: "REVIEW",
    2: "FAILURE",
    3: "UPLOADING_PDF"
};

class PatientConsultationEditContainer extends React.Component<
    PatientConsultationEditContainerProps,
    PatientConsultationEditContainerState
    > {
    private editPrescriptionReviewPdf: React.RefObject<EditPrescriptionReviewPdf>
    constructor(props: PatientConsultationEditContainerProps) {
        super(props);
        this.state = {
            modalScreen: MODAL_SCREENS["1"],
            modalStatus: false,
            consultationDetailsLoadingState: LOADING_STATE.NOT_INITIALIZED,
            pdfData: null,
            pdfPreviewData: null,
            isPdfDataSaved: null,
            errorMessage: null,
            secondModal: false,
            hasFetchedTemplates: false,
            hasFetchedTreatmentPlan: false,
            allTabs: [],
            activeTab: null
        };
        this.editPrescriptionReviewPdf = React.createRef();
    }

    componentDidMount() {
        this.props.clearCurrentChannel();
        this.props.getTreatmentPlans(this.props.doctorProfile.id);
        window.onbeforeunload = function (e: any) {
            let dialogText = "Are you sure?";
            e.returnValue = dialogText;
            return dialogText;
        };
        this.getStateFromProps(this.props)
    }

    componentDidUpdate() {
        if (!this.state.hasFetchedTreatmentPlan) {
            this.getConsultationTreatmentPlan()
        }
    }

    componentWillUnmount() {
        this.props.clearEditPrescriptionDetails()
        window.onbeforeunload = null;
    }

    checkAndInitializeConsultationDetails = (currProps: PatientConsultationEditContainerProps): boolean => {
        if (_isEmpty(currProps.editConsultation)) {
            RouteConfigs.RouteUtils.navigateToHrefUrl(null, RouteConfigs.RouteUtils.getPatientConsultationDetailsUrl(currProps.params.patientId, currProps.params.consultationId))
            return false
        }
        return true
    };

    getConsultationPdfTemplates = (appointmentSchedule: IAppointmentScheduleResponse) => {
        if (this.state && !this.state.hasFetchedTemplates) {
            this.props.fetchConsultationPdfTemplates(appointmentSchedule)
                .then(() => {
                    this.setState({
                        hasFetchedTemplates: true
                    })
                })
                .catch(() => {
                    this.setState({
                        hasFetchedTemplates: false
                    })
                });
        }
    }

    getConsultationTreatmentPlan = () => {
        if (!_isEmpty(this.props.editConsultation.medicalProfile) &&
            AppUtils.isDefined(this.props.editConsultation.medicalProfile.currentConsultationData.prescriptionDiagnosis)) {
            if (!_isEmpty(this.props.editConsultation.medicalProfile.currentConsultationData.prescriptionDiagnosis.diagnosticsList)) {
                let diagnosisArray: any[] = [];
                this.props.editConsultation.medicalProfile.currentConsultationData.prescriptionDiagnosis.diagnosticsList.map((diagnosis: any) => {
                    diagnosisArray = [...diagnosisArray, diagnosis.disease.internalValue]
                })
                this.props.fetchDataOnSelect(this.getDoctorId(), diagnosisArray)
            }
            this.setState({
                hasFetchedTreatmentPlan: true
            })
        }
    }

    getDoctorId = () => {
        return this.props.editConsultation && this.props.editConsultation.appointmentSchedule && this.props.editConsultation.appointmentSchedule.doctor
            ? this.props.editConsultation.appointmentSchedule.doctor.id
            : null;
    }

    getStateFromProps = (currProps: PatientConsultationEditContainerProps) => {
        if (this.checkAndInitializeConsultationDetails(currProps)) {
            this.getConsultationPdfTemplates(currProps.editConsultation.appointmentSchedule);
            let isSelf = this.props.patientDetails.profile.relationship && this.props.patientDetails.profile.relationship.toUpperCase() === AppConstants.patientRelationships.SELF;
            const allTabs = [TabManager.ConsultationTabConfig[EditPrescriptionTabKeyName.EDIT_PRESCRIPTION]]
            this.setState({
                allTabs,
                activeTab: allTabs[0]
            })
            this.props.fetchEditConsultationDetails(
                this.getConsultationId(),
                this.getPatientId(),
                this.getCurefitUserId(),
                isSelf,
                this.props.doctorProfile,
                this.props.editConsultation.appointmentSchedule.consultationProduct.subServiceType
            ).then((response: IEditConsultationResponse) => {
                this.props.updateEditConsultationDetails(response);
                this.setState({
                    consultationDetailsLoadingState: LOADING_STATE.LOADED
                })
            })
        }
    };

    getCurefitUserId = (): number => {
        return parseInt(this.props.patientDetails.profile.curefitUserId);
    };

    getPatientId = (): number => {
        let patientId = parseInt(this.props.params.patientId);
        if (
            !_isEmpty(this.props.editConsultation) &&
            !_isEmpty(this.props.editConsultation.appointmentSchedule) &&
            !_isEmpty(this.props.editConsultation.appointmentSchedule.patient)
        )
            patientId = parseInt(
                this.props.editConsultation.appointmentSchedule.patient.id.toString()
            );
        return patientId;
    };

    getConsultationId = (): number => {
        let consultationId = parseInt(this.props.params.consultationId);
        if (
            !_isEmpty(this.props.editConsultation) &&
            !_isEmpty(this.props.editConsultation.appointmentSchedule)
        )
            consultationId = parseInt(
                this.props.editConsultation.appointmentSchedule.id.toString()
            );
        return consultationId;
    };

    getBookingId = (): number => {
        return this.props.editConsultation.appointmentSchedule.bookingId;
    };

    toggleModal = (
        flag: boolean,
        clearPdfData?: boolean,
        modalScreen?: string,
        errorMessage?: string
    ) => {
        this.setState({
            modalScreen:
                modalScreen !== undefined ? modalScreen : this.state.modalScreen,
            modalStatus: flag,
            pdfData:
                clearPdfData !== undefined && clearPdfData ? null : this.state.pdfData,
            pdfPreviewData:
                clearPdfData !== undefined && clearPdfData
                    ? null
                    : this.state.pdfPreviewData,
            errorMessage
        });
    };

    navigateBackToDetails = () => {
        RouteConfigs.RouteUtils.navigateToHrefUrl(null, RouteConfigs.RouteUtils.getPatientConsultationDetailsUrl(this.props.params.patientId, this.props.params.consultationId))
    };

    getActiveTabProps() {
        let consultationType = !_isEmpty(this.props.editConsultation.appointmentSchedule) ? !_isEmpty(this.props.editConsultation.appointmentSchedule.rootBooking) ? this.props.editConsultation.appointmentSchedule.rootBooking.subCategoryCode : null : null;
        let doctorType = !_isEmpty(this.props.editConsultation.appointmentSchedule) ? this.props.editConsultation.appointmentSchedule.consultationProduct.subServiceType : null;
        let isSelf = this.props.patientDetails.profile.relationship && this.props.patientDetails.profile.relationship.toUpperCase() === AppConstants.patientRelationships.SELF;
        let currentPrescription = this.props.editConsultation.currentPrescription
        if (currentPrescription === null && !AppUtils.isPhysiotherapist(doctorType)) {
            let reasonForVisit = (this.props.editConsultation.appointmentSchedule && this.props.editConsultation.appointmentSchedule.followUpConsultationId
                && this.props.editConsultation.appointmentSchedule.followUpConsultationId !== -1)
                ? "Follow Up"
                : (this.props.editConsultation.appointmentSchedule && this.props.editConsultation.appointmentSchedule.rootBooking
                    && this.props.editConsultation.appointmentSchedule.rootBooking !== null
                    && this.props.editConsultation.appointmentSchedule.rootBooking.subCategoryCode === "HCU" || this.props.editConsultation.appointmentSchedule.rootBooking.subCategoryCode === "HCU_PACK")
                    ? "HCU"
                    : "";
            currentPrescription = {
                ...currentPrescription,
                reasonForVisit,
                publicNotes: "",
                privateNotes: "",
                noPresentingIllness: false
            }
        }
        let patientPastConditions = this.props.editConsultation.pastConditions && this.props.editConsultation.pastConditions.conditionsList
            ? this.props.editConsultation.pastConditions.conditionsList.map((pastData: any) => {
                return pastData.disease.displayValue;
            }) : null;

        switch (this.state.activeTab.tabName) {
            case ConsultationTabNames.PRESCRIPTION:
                return TabManager.getPatientPrescriptionTabProps(
                    AppUtils.getEditedBy(this.props.accountData),
                    this.getPatientId(),
                    this.getConsultationId(),
                    consultationType,
                    this.props.patientVitals,
                    this.props.editConsultation.allergies,
                    this.props.editConsultation.chiefComplaints,
                    this.props.editConsultation.redFlags,
                    patientPastConditions,
                    _isEmpty(this.props.editConsultation.currentMedication) ? [] :
                        this.props.editConsultation.currentMedication.medicationList,
                    this.props.editConsultation,
                    (prescription: IPrescription) => { },
                    (input: string) => { return Promise.reject() },
                    (input: string) => { return Promise.reject() },
                    (input: string) => { return Promise.reject() },
                    this.props.staticSpecialities,
                    this.props.staticMedicineTypes,
                    this.props.editConsultation.uiConfiguration,
                    this.props.editConsultation.medicalProfile,
                    isSelf,
                    this.getCurefitUserId().toString(),
                    (patientId: number, patientMedicalProfile: any, appointmentId: number, doctorType: string) => { return Promise.reject() },
                    this.toggleModal,
                    doctorType,
                    (bookingId: number, appointmentId: number, status: ConsultationStatusConstant) => { return Promise.reject() },
                    (patientMedicalProfile: IEHRSavePatientMedicalProfile) => { },
                    this.getBookingId(),
                    () => { },
                    (file: File) => { },
                    this.props.suggestions,
                    metricIdsToFetchMetrics,
                    this.props.fetchPatientVitals,
                    this.props.updateCurrentConsultationData,
                    this.fetchDataOnSelect,
                    this.props.featureConfigInfo,
                    this.props.editConsultation.appointmentSchedule,
                    PrescriptionTabModes.EDIT,
                    this.navigateBackToDetails
                );
            default:
                return {}
        }
    }

    decideModalSize = (): string => {
        if (
            this.state.modalScreen === MODAL_SCREENS["1"]
        )
            return "full";
        else return "small";
    };

    onPrescriptionSaveFail = (
        errorText: string,
        isPdfDataSaved: boolean
    ) => {
        this.setState({
            modalScreen: MODAL_SCREENS[2],
            prescriptionSaveError: errorText,
            isPdfDataSaved
        });
    };

    onPrescriptionSaveSuccess = (pdfData: File, pdfPreviewData: File) => {
        this.setState({
            pdfData,
            pdfPreviewData
        });
    };

    onPlanSaveSuccess = (pdfData: File, pdfPreviewData: File) => {
        this.setState({
            pdfData,
            pdfPreviewData
        });
    };

    toggleSecondModal = (value: boolean) => {
        this.setState({ secondModal: value });
    };

    getPdfHtmlContent = () => {
        console.log(document.getElementsByClassName("review-prescription-container")[0].innerHTML);
        return document.getElementsByClassName("review-prescription-container")[0]
            .innerHTML;
    };

    getHTMLTemplate = (): string => {
        const template = this.getPdfHtmlContent().replace(
            /\/images/gi,
            `./dist/server/images`
        );
        const domain = window.location.origin;
        const htmlDoc = htmlDocTemplate
            .replace(/DOMAIN/gi, domain)
            .replace(`$injectBody`, template);
        return htmlDoc;
    };

    handleUploadPdf = () => {
        ApiManager.getOrionApis().requestPdfGeneration(
            this.getHTMLTemplate(),
            ConsultationStatusConstant.EDIT_PRESCRIPTION_COMPLETED,
            this.getBookingId(),
            this.getConsultationId(),
            this.props.editConsultation.appointmentSchedule.consultationProduct.isPlan,
            AlbusUrlConfigs.getDoctorSigUrl(
                this.props.editConsultation.appointmentSchedule.doctor
            )
        ).then(() => {
            this.navigateBackToDetails()
        })
            .catch(error => {
                console.log("Upload Error", error)
            })
    };

    clearErrorMessage = () => {
        this.setState({
            errorMessage: null
        })
    }

    updateActiveTab = (): void => { }

    fetchDataOnSelect = (diagnosis?: Array<string>) => {
        this.props.fetchDataOnSelect(this.getDoctorId(), diagnosis)
    }

    render() {
        let myInstance = this;
        let isComponentLoadedCompletely =
            myInstance.state.consultationDetailsLoadingState === LOADING_STATE.LOADED && myInstance.state.hasFetchedTemplates && myInstance.state.hasFetchedTreatmentPlan;
        let modalScreen = this.state.modalScreen;
        let currentAppointment, currPatient, activeTabProps, ActiveTabComponent, sellableProductInfo, city,
            videoContext = null;
        if (isComponentLoadedCompletely) {
            currentAppointment = this.props.editConsultation.appointmentSchedule;
            currPatient = this.props.editConsultation.appointmentSchedule.patient;
            ActiveTabComponent = this.state.activeTab.component;
            activeTabProps = { ...this.getActiveTabProps() }
            sellableProductInfo = this.props.editConsultation.baseSellableProduct;
            videoContext = currentAppointment.appointmentActionsWithContext && currentAppointment.appointmentActionsWithContext.videoActionWithContext
                && currentAppointment.appointmentActionsWithContext.videoActionWithContext
                    .context;
        }
        let currentMedicalProfile = this.props.editConsultation.medicalProfile
        let doctorType = !_isEmpty(this.props.editConsultation.appointmentSchedule) ? this.props.editConsultation.appointmentSchedule.consultationProduct.subServiceType : null;
        const isMobile = AppUtils.getIsMobile();
        return (
            <div>
                <FlowContext.Provider value={EventNames.Edit_Prescription}>
                    <SectionContext.Provider value={EventNames.Prescription}>
                        {isComponentLoadedCompletely && (
                            <div className="ongoing-consultation-cont">
                                <div className="ongoing-consultation-wrapper">
                                    <TopBanner
                                        patient={currPatient}
                                        patientProfile={this.props.patientDetails.profile}
                                        baseSellableProduct={sellableProductInfo}
                                        appointmentSchedule={currentAppointment as any}
                                        TabsComponent={!isMobile && (<TabsHeader tabs={this.state.allTabs} activeTab={this.state.activeTab}
                                            updateActiveTab={this.updateActiveTab} trackerEvents={{ flow: EventNames.Edit_Prescription, referenceID: this.getConsultationId().toString() }} />)}
                                        renderOptions={{
                                            timer: false,
                                            phoneCall: false
                                        }}
                                    />
                                    <div className="ongoing-consultation-main-cont">
                                        <div className={"consultation-details-container"}>
                                            {isMobile && (
                                                <TabsHeader
                                                    tabs={this.state.allTabs}
                                                    activeTab={this.state.activeTab}
                                                    updateActiveTab={this.updateActiveTab}
                                                    trackerEvents={{ flow: EventNames.Edit_Prescription, referenceID: this.getConsultationId().toString() }}
                                                />
                                            )}
                                            <div className="tab-content edit-prescription">
                                                <ActiveTabComponent {...activeTabProps} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {this.state.modalStatus && (
                                    <ModalComponent modalSize={this.decideModalSize()}>
                                        {
                                            (modalScreen === MODAL_SCREENS["1"] &&
                                                <EditPrescriptionReviewPdf
                                                    ref={this.editPrescriptionReviewPdf}
                                                    ongoingConsultation={{ ...this.props.editConsultation, medicalProfile: currentMedicalProfile }}
                                                    patientVitals={this.props.patientVitals}
                                                    staticSpecialities={this.props.staticSpecialities}
                                                    toggleModal={this.toggleModal}
                                                    patientProfile={this.props.patientDetails.profile}
                                                    savePrescriptionNew={this.props.savePatientPlan}
                                                    isPdfDataSaved={this.state.isPdfDataSaved}
                                                    onPrescriptionSaveFail={this.onPrescriptionSaveFail}
                                                    onPrescriptionSaveSuccess={this.handleUploadPdf}
                                                    pdfTemplates={this.props.pdfTemplates}
                                                />
                                            )
                                        }
                                        {(modalScreen === MODAL_SCREENS["2"] &&
                                            <PdfSaveFailureModal
                                                doctorInfo={this.props.doctorProfile}
                                                bookingId={this.getBookingId()}
                                                consultationId={this.getConsultationId()}
                                                patientDetails={this.props.patientDetails}
                                                ongoingConsultation={{ ...this.props.editConsultation, medicalProfile: currentMedicalProfile }}
                                                toggleModal={this.toggleModal}
                                                showDifferentialDiagnosisModal={this.navigateBackToDetails}
                                                pdfData={this.state.pdfData}
                                                pdfPreviewData={this.state.pdfPreviewData}
                                                prescriptionSaveError={this.state.prescriptionSaveError}
                                                savePrescription={(appointmentId: number, prescription: IPrescription) => Promise.reject()}
                                                uploadPrescription={this.props.uploadPrescription}
                                                uploadPlan={this.props.uploadPlan}
                                                updateAppointmentStatus={(bookingId: number, appointmentId: number, status: ConsultationStatusConstant) => Promise.reject()}
                                                onPrescriptionSaveSuccess={this.onPrescriptionSaveSuccess}
                                                isPdfDataSaved={this.state.isPdfDataSaved}
                                                savePrescriptionNew={(consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string) => Promise.reject()}
                                                removeWorkflowObject={(consultation: IAppointmentScheduleResponse, isPrescriptionObject: boolean) => Promise.reject()}
                                                planSaveError={""}
                                                onPlanSaveSuccess={this.onPlanSaveSuccess}
                                                errorMessage={
                                                    AppUtils.isDefined(this.state.errorMessage)
                                                        ? this.state.errorMessage
                                                        : this.state.isPdfDataSaved
                                                            ? AppMessages.PRESCRIPTION_PDF_SAVE_FAILURE
                                                            : AppMessages.SAVE_PRESCRIPTION_UNSUCCESSFUL
                                                }
                                                saveUserPlan={(consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string) => Promise.reject()}
                                                isOnlyErrorModal={AppUtils.isDefined(
                                                    this.state.errorMessage
                                                )}
                                            />
                                        )
                                        }
                                        {
                                            modalScreen === MODAL_SCREENS["3"] && (
                                                <div className={"saving-pdf-modal"}>
                                                    <span>
                                                        <img src={ORION_IMG_URL + "loader/rolling-loader.gif"} />
                                                        <div>Saving PDF</div>
                                                    </span>
                                                </div>
                                            )}
                                    </ModalComponent>
                                )}
                            </div>
                        )}
                        {!isComponentLoadedCompletely && (
                            <div className="central-loader ongoing-consultation-cont-loader">
                                <img src={ORION_IMG_URL + "loader/curefit-loader.gif"} />
                            </div>
                        )}
                    </SectionContext.Provider>
                </FlowContext.Provider>
            </div>
        );
    }
}

function mapStateToProps(state: IAppState, ownProps: any): any {
    return {
        accountData: state.accountData,
        editConsultation: state.editPrescription,
        patientDetails: state.patientCompleteInfo,
        doctorProfile: state.doctorProfile,
        patientVitals: state.patientCompleteInfo.vitals,
        patientGeneralExamination: state.patientCompleteInfo.generalExaminations,
        patientSystemicExamination: state.patientCompleteInfo.systemicExaminations,
        staticSpecialities: state.allStaticDataNew.doctorSpecialityResponseList,
        staticMedicineTypes: state.allStaticDataNew.medicalTypeList,
        pdfTemplates: state.editPrescription.pdfTemplates,
        suggestions: state.editPrescription.suggestions,
        staticGoals: state.allStaticDataNew.goals,
        staticMetrics: state.allStaticDataNew.metrics,
        featureConfigInfo: state.featureConfigInfo
    }
}

function mapDispatchToProps(dispatch: any): any {
    return {
        uploadPrescription: (
            appointmentId: number,
            prescription: File,
            type: string
        ) => {
            return EditPrescriptionActions.savePatientPrescriptionPDF(
                appointmentId,
                prescription,
                type
            );
        },
        uploadPlan: (appointmentId: number, patientPlan: File, type: string) => {
            return EditPrescriptionActions.savePatientPlanPDF(
                appointmentId,
                patientPlan,
                type
            );
        },
        fetchPatientVitals: (
            patientId: number,
            userId: number,
            metricIds: number[]
        ) => {
            return PatientCompleteInfoActions.fetchPatientVitals(
                dispatch,
                patientId,
                userId,
                metricIds
            );
        },
        clearCurrentChannel: () => {
            return dispatch(TwilioActions.clearCurrentChannel());
        },
        fetchConsultationPdfTemplates: (appointmentSchedule: IAppointmentScheduleResponse) => {
            return EditPrescriptionActions.fetchConsultationPdfTemplates(dispatch, appointmentSchedule)
        },
        fetchEditConsultationDetails: (appointmentId: number, patientId: number, userId: number, isSelf: boolean, doctorProfile: IDoctorProfile, subServiceType: string): Promise<IEditConsultationResponse> => {
            return EditPrescriptionActions.fetchEditConsultationDetails(dispatch, patientId, appointmentId, userId, isSelf, doctorProfile, subServiceType);
        },
        clearEditPrescriptionDetails: () => {
            return dispatch(EditPrescriptionActions.clearEditPrescriptionDetails());
        },
        savePatientPlan: (
            consultation: IAppointmentScheduleResponse,
            prescription: IPrescription,
            patientPlan: IPatientLifestylePlanWrapper,
            planId: string,
            isSelf?: boolean,
            medicalProfile?: any
        ) => {
            return EditPrescriptionActions.savePatientPlanNew(
                consultation,
                prescription,
                patientPlan,
                planId,
                ConsultationStatusConstant.EDIT_PRESCRIPTION_GENERATED,
                isSelf,
                medicalProfile
            );
        },
        updateCurrentConsultationData: (medicalProfile: IEHRSavePatientMedicalProfile) => {
            return dispatch(EditPrescriptionActions.updateCurrentConsultationData(medicalProfile))
        },
        updateEditConsultationDetails: (response: IEditConsultationResponse) => {
            return dispatch(EditPrescriptionActions.updateEditConsultationDetails(response))
        },
        fetchDataOnSelect: (doctorId: number, diagnosis?: Array<string>) => {
            EditPrescriptionActions.fetchTreatmentPlanSuggestions(dispatch, doctorId, diagnosis)
        },
        getTreatmentPlans: (doctorId: number) => {
            TreatmentPlanActions.fetchAllTreatmentPlanTemplates(doctorId, dispatch);
        },
    }
}


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PatientConsultationEditContainer)
